import { Dialog } from "primereact/dialog";
import { Filter, serializeFilter } from "./filter";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { InputText } from "primereact/inputtext";
import { User } from "../../../client";
import { useUsers } from "../data";
import { displayNameForUser } from "../../../util";
import { Checkbox } from "primereact/checkbox";
import { useEffect, useState } from "react";
import styles from "./FilterModal.module.css";
import { readableStatus } from "./util";
import { ReportStatus } from "../../../api/types";
import { Divider } from "primereact/divider";
import { useAddSavedFilter } from "../data/saved-filter";
import { useFilters } from "./filter-hooks";

export const ManageFilterModal = ({
  visible,
  onHide,
}: {
  visible: boolean;
  onHide: () => void;
}) => {
  const filters = useFilters();
  const users = useUsers();

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Manage filters"
      style={{ width: "800px", minHeight: "400px" }}
    >
      <DataTable value={filters} editMode="cell">
        <Column
          header="Name (click to edit)"
          style={{ width: "300px" }}
          field="name"
          editor={(o) => (
            <InputText
              style={{ width: "calc(100% - 0.5rem)", padding: "8px 0px" }}
              value={o.value ?? ""}
              onChange={(e) => o.editorCallback?.(e.target.value)}
            />
          )}
          cellEditValidator={(e) => e.newValue.length > 0}
          onCellEditComplete={(e) => {
            if (e.newValue) {
              (e.rowData as (typeof filters)[number]).setName(e.newValue);
            }
          }}
        />
        <Column
          header="Preview"
          style={{ width: "300px" }}
          body={(val: (typeof filters)[number]) =>
            previewFilter(val.filter, users?.data?.all_company_users ?? [])
          }
        ></Column>
        <Column
          header="Default"
          align="center"
          body={(val: (typeof filters)[number]) => (
            <RadioButton
              checked={val.isDefault}
              onClick={(e) => {
                val.setDefault(!!e.checked);
              }}
            />
          )}
        ></Column>
        <Column
          header="Delete"
          body={(val: (typeof filters)[number]) => (
            <Button
              size="small"
              outlined
              onClick={val.remove}
              tooltip="Delete"
              tooltipOptions={{ showDelay: 500 }}
              icon="pi pi-trash"
              severity="danger"
            />
          )}
        ></Column>
      </DataTable>
    </Dialog>
  );
};

export const AddFilterModal = ({
  filter,
  onHide,
}: {
  filter: Filter | null;
  onHide: () => void;
}) => {
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const users = useUsers();
  const addSavedFilter = useAddSavedFilter();

  useEffect(() => {
    if (filter) {
      setName("");
      setIsDefault(false);
    }
  }, [filter]);

  return (
    <Dialog
      visible={!!filter}
      onHide={onHide}
      header="Add filter"
      style={{ width: "600px" }}
    >
      {filter ? (
        <div className={styles.container}>
          <div className={styles.col}>
            <b>Filter for</b>
            <div>
              {previewFilter(filter, users?.data?.all_company_users ?? [])}
            </div>
          </div>
          <Divider />
          <div className={styles.col}>
            <b>
              <label htmlFor="name">Filter name</label>
            </b>
            <InputText
              placeholder="name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              id="name"
            />
          </div>
          <div className={styles.row}>
            <Checkbox
              checked={isDefault}
              onChange={(e) => setIsDefault(!!e.checked)}
              inputId="isDefault"
            />
            <b>
              <label htmlFor="isDefault">Make this my default</label>
            </b>
          </div>
          <Button
            style={{ marginTop: "24px" }}
            label="Save"
            disabled={!name || addSavedFilter.isPending}
            loading={addSavedFilter.isPending}
            onClick={async () => {
              await addSavedFilter.mutateAsync({
                isDefault,
                nickname: name,
                url: serializeFilter(filter),
              });
              onHide();
            }}
          />
        </div>
      ) : null}
    </Dialog>
  );
};

const previewFilter = (filter: Filter, users: User[]): string => {
  const parts: string[] = [];

  if (filter.setFields.includes("selectedStatuses")) {
    const statuses = filter.selectedStatuses
      .map((s) =>
        (Object.values(ReportStatus) as string[]).includes(s)
          ? readableStatus(s as ReportStatus)
          : undefined
      )
      .filter((s) => !!s);

    if (statuses.length > 1) {
      parts.push(`status in (${statuses.join(", ")})`);
    } else if (statuses.length === 1) {
      parts.push(`status is "${statuses[0]}"`);
    }
  }

  if (filter.setFields.includes("selectedAssigneeUserIds")) {
    const names = filter.selectedAssigneeUserIds
      .map((id) =>
        id === "Unassigned" ? "Unassigned" : users.find((u) => u.id === id)
      )
      .filter((u) => !!u)
      .map((u) => (u === "Unassigned" ? "Unassigned" : displayNameForUser(u)));

    if (names.length > 1) {
      parts.push(`assigned to one of (${names.join(", ")})`);
    } else if (names.length === 1) {
      if (names[0] === "Unassigned") {
        parts.push(`unassigned`);
      } else {
        parts.push(`assigned to ${names[0]}`);
      }
    }
  }

  if (filter.setFields.includes("selectedHandlerUserIds")) {
    const names = filter.selectedHandlerUserIds
      .map((id) =>
        id === "Unassigned" ? "Unassigned" : users.find((u) => u.id === id)
      )
      .filter((u) => !!u)
      .map((u) => (u === "Unassigned" ? "Unassigned" : displayNameForUser(u)));

    if (names.length > 1) {
      parts.push(`handled by one of (${names.join(", ")})`);
    } else if (names.length === 1) {
      if (names[0] === "Unassigned") {
        parts.push(`unassigned handler`);
      } else {
        parts.push(`handled by ${names[0]}`);
      }
    }
  }

  if (
    filter.setFields.includes("initialSortOrder") ||
    filter.setFields.includes("initialSortField")
  ) {
    parts.push(
      `sorted by ${filter.initialSortField} ${
        filter.initialSortOrder === "asc" ? "ascending" : "descending"
      }`
    );
  }

  return parts.join(" and ");
};
